import React, { useEffect, useState } from 'react';
import Layout from './Layout';
import './css/Branch.css';

const BranchPage = () => {
  const [user, setUser] = useState(null);
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form state
  const [formData, setFormData] = useState({
    name: ''
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredBranches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBranches = filteredBranches.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
    loadBranches();
  }, []);

  useEffect(() => {
    if (filterText.trim() === '') {
      setFilteredBranches(branches);
    } else {
      const filtered = branches.filter(branch =>
        branch.NAME.toLowerCase().includes(filterText.toLowerCase())
      );
      setFilteredBranches(filtered);
    }
    setCurrentPage(1);
  }, [filterText, branches]);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/branches');
      const data = await response.json();

      if (data.success) {
        setBranches(data.branches);
        setFilteredBranches(data.branches);
      } else {
        setError('Failed to load branches');
      }
    } catch (err) {
      setError('Error loading branches: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilterText('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const openAddModal = () => {
    setModalMode('add');
    setFormData({ name: '' });
    setSelectedBranch(null);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (branch) => {
    setModalMode('edit');
    setFormData({ name: branch.NAME || '' });
    setSelectedBranch(branch);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBranch(null);
    setFormData({ name: '' });
    setError('');
    setSuccess('');
  };

  const openDeleteModal = (branch, e) => {
    e.stopPropagation();
    setBranchToDelete(branch);
    setShowDeleteModal(true);
    setError('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setBranchToDelete(null);
    setError('');
  };

  const handleDelete = async () => {
    if (!branchToDelete) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`/api/branches/${branchToDelete.pkkey}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Branch deleted successfully');
        loadBranches();
        closeDeleteModal();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error deleting branch: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (branch) => openEditModal(branch);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Branch name is required');
      return;
    }

    const submitData = { name: formData.name };

    try {
      setLoading(true);
      setError('');

      const url = modalMode === 'add'
        ? '/api/branches/newbranch'
        : `/api/branches/savebranch/${selectedBranch.pkkey}`;

      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(data.message);
        loadBranches();
        setTimeout(() => closeModal(), 1000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="homepage-container">
        <div className="homepage-header">
          <h1 className="homepage-title">Branch Management</h1>
        </div>

        {success && !showModal && !showDeleteModal && (
          <div className="success-message">{success}</div>
        )}

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-controls">
            <div>
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="filter-input"
                placeholder="Enter Branch Name to filter..."
              />
            </div>
            <button onClick={handleReset} className="reset-button">Reset</button>
          </div>

          <div>
            <button onClick={openAddModal} className="new-button">+ New Branch</button>
          </div>
        </div>

        {/* Table */}
        <div>
          {loading && <div className="loading-message">Loading...</div>}
          {error && !showModal && !showDeleteModal && (
            <div className="error-message">{error}</div>
          )}

          <table className="users-table">
            <thead>
              <tr className="table-header">
                <th>No.</th>
                <th>Branch Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentBranches.map((branch, index) => (
                <tr
                  key={branch.pkkey}
                  onClick={() => handleRowClick(branch)}
                  className="table-row"
                >
                  <td className="table-cell">{startIndex + index + 1}.</td>
                  <td className="table-cell">{branch.NAME}</td>
                  <td className="table-cell">
                    <button
                      className="delete-button"
                      onClick={(e) => openDeleteModal(branch, e)}
                      disabled={loading}
                      title="Delete Branch"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredBranches.length === 0 && !loading && (
            <div className="no-data-message">No branches found</div>
          )}

          {/* Pagination */}
          {filteredBranches.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredBranches.length)} of {filteredBranches.length} entries
              </div>

              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="pagination-ellipsis">...</span>
                    ) : (
                      <button
                        className={`pagination-button ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}

                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">
                {modalMode === 'add' ? 'Add New Branch' : 'Edit Branch'}
              </h2>

              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Branch Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    autoComplete="off"
                    placeholder="e.g., Johor Bahru, KL, Penang"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (modalMode === 'add' ? 'Add Branch' : 'Save Branch')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Confirm Delete</h2>

              {error && <div className="modal-error">{error}</div>}

              <div className="delete-confirmation">
                <p>Are you sure you want to delete this branch?</p>
                <div className="client-details">
                  <strong>Branch Name:</strong> {branchToDelete?.NAME}
                </div>
                <p className="warning-text">This action cannot be undone.</p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="delete-confirm-button"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BranchPage;